import Keycloak from 'keycloak-js'

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.dev.intuigence.ai/auth',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'intuigence-dev',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'intuigence-web',
}

export const keycloak = new Keycloak(keycloakConfig)

const MIN_TOKEN_VALIDITY_SECONDS = 60

/**
 * FS-701: how long to wait for `keycloak.init()` before giving up on it.
 *
 * `onLoad: 'check-sso'` resolves only when the hidden `/silent-check-sso.html`
 * iframe posts a message back. keycloak-js 26.2.4 puts no `onerror` and no
 * timeout on that iframe (`#checkSsoSilently`, lib/keycloak.js:974) — its one
 * `applyTimeoutToPromise` guards a different call site — so if those four bytes
 * never arrive, the promise neither resolves nor rejects. It hangs, and every
 * caller below it hangs with it: `initializeAuth`, then Login's effect, then the
 * spinner, forever. A 502 from the edge did exactly that on 2026-09-08.
 *
 * Rejecting is all that is needed, because every caller already handles
 * rejection correctly — `initializeAuth` returns false and Login renders the
 * buttons. The bug was never the error handling; it was that there was no error.
 *
 * 15s is the top of the range the issue endorses, chosen because a false
 * positive is cheap and a real one is not: bailing early on a user who *did*
 * have a live SSO session costs them one click (the button redirects straight
 * through), while waiting too long is the defect being fixed.
 */
const INIT_TIMEOUT_MS = 15_000

let initializationPromise: Promise<boolean> | null = null
let isInitialized = false
let onAuthStateChange: ((authenticated: boolean) => void) | null = null

export const setAuthStateChangeCallback = (callback: (authenticated: boolean) => void) => {
  onAuthStateChange = callback
}

const setupEventHandlers = () => {
  keycloak.onAuthSuccess = () => {
    onAuthStateChange?.(true)
  }

  keycloak.onAuthRefreshSuccess = () => {
    onAuthStateChange?.(true)
  }

  keycloak.onAuthRefreshError = () => {
    console.error('Keycloak: session expired')
    onAuthStateChange?.(false)
  }

  keycloak.onAuthLogout = () => {
    onAuthStateChange?.(false)
  }

  keycloak.onTokenExpired = () => {
    keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS).catch(() => {})
  }
}

export const initKeycloak = async (): Promise<boolean> => {
  if (isInitialized) {
    return keycloak.authenticated || false
  }
  
  if (initializationPromise) {
    return initializationPromise
  }
  
  setupEventHandlers()
  
  initializationPromise = (async () => {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    try {
      const authenticated = await Promise.race([
        keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          pkceMethod: 'S256',
          checkLoginIframe: false,
        }),
        new Promise<never>((_, reject) => {
          timeoutHandle = setTimeout(
            () => reject(new Error(`Keycloak init timed out after ${INIT_TIMEOUT_MS}ms`)),
            INIT_TIMEOUT_MS,
          )
        }),
      ])
      
      isInitialized = true
      return authenticated
    } catch (error) {
      // Keycloak adapter can only be initialized once per page load
      if (error instanceof Error && error.message.includes('can only be initialized once')) {
        isInitialized = true
        return keycloak.authenticated || false
      }
      
      // For other errors (network, config, and now the timeout above), allow
      // retry by clearing state. A retry after a timeout that later settled
      // hits the "can only be initialized once" branch above, which is correct.
      initializationPromise = null
      throw error
    } finally {
      clearTimeout(timeoutHandle)
    }
  })()
  
  return initializationPromise
}

export const login = (redirectUri?: string, idpHint?: string) => {
  keycloak.login({
    redirectUri: redirectUri || window.location.origin + '/chat',
    idpHint: idpHint,
  })
}

export const loginWithGoogle = (redirectUri?: string) => {
  login(redirectUri, 'google')
}

export const loginWithMicrosoft = (redirectUri?: string) => {
  login(redirectUri, 'microsoft')
}

export const register = (redirectUri?: string) => {
  keycloak.register({
    redirectUri: redirectUri || window.location.origin + '/chat',
  })
}

export const logout = () => {
  keycloak.logout({
    redirectUri: window.location.origin,
  })
}

export const getToken = (): string | null => {
  return keycloak.token || null
}

export const isAuthenticated = (): boolean => {
  return keycloak.authenticated || false
}

export const isKeycloakInitialized = (): boolean => {
  return isInitialized
}

export const ensureFreshToken = async (): Promise<string | null> => {
  if (!keycloak.authenticated) {
    return null
  }
  
  try {
    await keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS)
    return keycloak.token || null
  } catch {
    return null
  }
}

/** @deprecated Use ensureFreshToken() instead */
export const updateToken = ensureFreshToken

export const forceTokenRefresh = async (): Promise<string | null> => {
  if (!keycloak.authenticated) {
    return null
  }

  try {
    await keycloak.updateToken(-1)
    return keycloak.token || null
  } catch {
    return null
  }
}

// Shape of the token response from the Phase Two keycloak-orgs
// switch-organization endpoint — same as Keycloak's /token endpoint.
interface SwitchOrganizationTokenResponse {
  access_token: string
  refresh_token?: string
  id_token?: string
}

/**
 * Switch the user's ACTIVE organization (tenant) via the Phase Two keycloak-orgs
 * extension.
 *
 * Calls `PUT {keycloakUrl}/realms/{realm}/users/switch-organization` with the
 * current access token; the server persists the choice (the `org.ro.active`
 * attribute) and returns a fresh token set whose `active_organization` claim
 * names the chosen org. We then apply the new tokens to the keycloak-js adapter
 * so every subsequent API call carries the switched token. The backend resolves
 * the served tenant from `active_organization` (see jwt.strategy.ts /
 * tenant_context.py), so the data scope follows the switch.
 *
 * The endpoint enforces membership server-side: switching to an org the user
 * does not belong to returns 401.
 *
 * @returns the new access token, or null if not authenticated.
 * @throws Error if the switch request fails (non-2xx).
 */
export const switchOrganization = async (organizationId: string): Promise<string | null> => {
  if (!keycloak.authenticated) {
    return null
  }

  // Ensure the token we authenticate the switch call with is fresh.
  const token = await ensureFreshToken()
  if (!token) {
    return null
  }

  const baseUrl = keycloakConfig.url.replace(/\/+$/, '')
  const url = `${baseUrl}/realms/${keycloakConfig.realm}/users/switch-organization`

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: organizationId }),
  })

  if (!response.ok) {
    throw new Error(`Failed to switch organization (HTTP ${response.status})`)
  }

  const data = (await response.json()) as SwitchOrganizationTokenResponse
  if (!data.access_token) {
    throw new Error('Switch organization response missing access_token')
  }

  // Apply the new token set to the adapter. keycloak-js exposes token fields as
  // mutable; setting them (and the parsed forms) makes getToken()/tokenParsed
  // reflect the switched org without a full re-init. updateToken() will continue
  // to refresh from the new refresh_token going forward.
  keycloak.token = data.access_token
  keycloak.tokenParsed = decodeJwtPayload(data.access_token)
  if (data.refresh_token) {
    keycloak.refreshToken = data.refresh_token
    keycloak.refreshTokenParsed = decodeJwtPayload(data.refresh_token)
  }
  if (data.id_token) {
    keycloak.idToken = data.id_token
    keycloak.idTokenParsed = decodeJwtPayload(data.id_token)
  }

  return data.access_token
}

/** Decode a JWT payload (middle segment) without verifying the signature. */
const decodeJwtPayload = (jwt: string): Keycloak.KeycloakTokenParsed | undefined => {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return undefined
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(''),
    )
    return JSON.parse(json) as Keycloak.KeycloakTokenParsed
  } catch {
    return undefined
  }
}

// Keycloak organization attributes structure: { [orgId]: { name, attributes: { attrName: value } } }
// Attribute values can be string or string[] (Keycloak returns arrays for multi-valued attributes)
interface OrganizationAttributesMap {
  [orgId: string]: {
    name?: string
    attributes?: Record<string, string | string[]>
  }
}

// Phase Two keycloak-orgs "Active Organization" claim, present once the user
// switches org via switchOrganization(). Note the singular `role` key.
interface ActiveOrganizationClaim {
  id?: string
  name?: string
  role?: string[]
}

interface KeycloakTokenWithClaims extends Keycloak.KeycloakTokenParsed {
  email?: string
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  preferred_username?: string
  organizations?: Record<string, { name?: string; roles?: string[] }>
  active_organization?: ActiveOrganizationClaim
  'organization-attributes'?: OrganizationAttributesMap
}

export interface OrganizationInfo {
  id: string
  name: string
  /**
   * Human-readable label the user originally entered. May be missing on
   * tokens minted before the org was created with a separate displayName
   * (legacy rows) — in that case the UI falls back to `name`. Sourced from
   * the `organization-attributes.<id>.attributes.displayName` JWT claim,
   * which is set by the backend at org creation.
   */
  displayName?: string
  roles: string[]
  attributes?: Record<string, string | string[]>
}

export interface KeycloakUserInfo {
  userId: string
  email: string
  name: string
  given_name: string
  family_name: string
  picture?: string
  preferred_username: string
  organizations: OrganizationInfo[]
  /**
   * The currently active organization — the one the backend serves data for.
   * Derived from the `active_organization` claim when present (set after a
   * tenant switch), else the first entry of `organizations`. This is the org
   * whose id becomes `tenant_id`.
   */
  primaryOrganization?: OrganizationInfo
  activeOrganizationId?: string
  role?: 'admin' | 'member' | 'annotator'
  tenant_id?: string
  isInternalOrg?: boolean
}

const parseOrganizations = (tokenParsed: KeycloakTokenWithClaims | undefined): OrganizationInfo[] => {
  if (!tokenParsed) return []

  const orgsData = tokenParsed.organizations
  if (!orgsData || typeof orgsData !== 'object' || Object.keys(orgsData).length === 0) {
    return []
  }

  const orgAttributesMap = tokenParsed['organization-attributes']

  return Object.entries(orgsData).map(([id, data]) => {
    // Keycloak attributes are always multi-valued (string[]). We persist
    // displayName as a single-valued attribute at org creation, so collapse
    // the array to its first element.
    const rawDisplayName = orgAttributesMap?.[id]?.attributes?.displayName
    const displayName = Array.isArray(rawDisplayName)
      ? rawDisplayName[0]
      : rawDisplayName
    return {
      id,
      name: data?.name || 'Unknown',
      displayName: displayName || undefined,
      roles: data?.roles || [],
    }
  })
}

export const getUserInfo = (): KeycloakUserInfo | null => {
  if (!keycloak.authenticated || !keycloak.tokenParsed) {
    return null
  }

  const token = keycloak.tokenParsed as KeycloakTokenWithClaims
  const organizations = parseOrganizations(token)

  // Resolve the active org. The `organizations` map is unordered and is NOT
  // reordered by a tenant switch, so prefer `active_organization` when present
  // and it names an org the user is actually a member of; otherwise fall back to
  // the first entry. Mirrors the backend resolution in jwt.strategy.ts /
  // tenant_context.py so the UI's "active" badge matches the served tenant.
  const activeOrgId = token.active_organization?.id
  const activeOrg =
    (activeOrgId && organizations.find((o) => o.id === activeOrgId)) || undefined
  const primaryOrg = activeOrg || (organizations.length > 0 ? organizations[0] : undefined)
  // Precedence must match the backend deriveOrgRole (admin > annotator > member).
  const role = primaryOrg
    ? primaryOrg.roles?.includes('admin')
      ? 'admin'
      : primaryOrg.roles?.includes('annotator')
        ? 'annotator'
        : 'member'
    : undefined

  // Check if the organization has internalOrg attribute set to 'true'
  const orgAttributesMap = token['organization-attributes']
  let isInternalOrg = false
  if (orgAttributesMap && primaryOrg) {
    const internalOrgValue = orgAttributesMap[primaryOrg.id]?.attributes?.internalOrg
    // Keycloak returns multi-valued attributes as arrays, so check both string and array formats
    isInternalOrg = internalOrgValue === 'true' ||
      (Array.isArray(internalOrgValue) && internalOrgValue.includes('true'))
  }

  return {
    userId: token.sub || '',
    email: token.email || '',
    name: token.name || token.preferred_username || '',
    given_name: token.given_name || '',
    family_name: token.family_name || '',
    picture: token.picture,
    preferred_username: token.preferred_username || token.email || '',
    organizations,
    primaryOrganization: primaryOrg,
    activeOrganizationId: primaryOrg?.id,
    role,
    tenant_id: primaryOrg?.id,
    isInternalOrg,
  }
}

// Dev-only helper: expose token + workspace lookup on `window.__intui` so local
// scripts (e.g. tools/scripts/pid-qa-eval.sh) can guide users to copy a fresh
// bearer token from the browser console without hunting through the Network tab.
// Tree-shaken out of production builds via `import.meta.env.DEV`.
if (import.meta.env.DEV) {
  const intui = {
    /** Returns the current Keycloak access token (or null if not authenticated). */
    token: (): string | null => keycloak.token || null,
    /** Fetches the user's workspaces using the current token. */
    workspaces: async (): Promise<unknown> => {
      const t = keycloak.token
      if (!t) throw new Error('Not authenticated')
      const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:8080').replace(/\/+$/, '')
      const res = await fetch(`${apiBase}/v1/workspaces`, {
        headers: { Authorization: `Bearer ${t}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  }
  // @ts-expect-error attaching dev helper to window
  window.__intui = intui
  console.log(
    '%c[__intui]%c dev helpers ready: __intui.token(), __intui.workspaces()',
    'color:#0af;font-weight:bold',
    'color:inherit',
  )
}

export default keycloak
